

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    Radio,
    RadioGroup,
    Tab,
    Tabs,
    TextField,
    Typography,
    CircularProgress,
    Alert
} from '@mui/material';
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import CloseIcon from "@mui/icons-material/Close";
import FilePreviewComponent from '../../../botBuilder/FilePreviewComponent';
import { apiGatewayAxiosInstance } from '../../../../utils/axios';
import { DYNAMIC_URL_REGEX } from '../../../../constants';
import { CloseOutlined } from "@mui/icons-material";
import CheckIcon from '@mui/icons-material/Check';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';
import VariableIcon from '@mui/icons-material/Code';
import { MediaDisplay } from './MediaDisplay';

export default function SelectMediaModel({
    openMediaDialog,
    setOpenMediaDialog,
    fieldName,
    state,
    url,
    type,
    previewImage,
    deleteFile,
    formikImage,
    isDisabled,
    aspect,
    onSelectImage,
    width,
    height,
    imageHeightRatio,
    imageWidthRatio,
    inputOnChange,

    error,
    handleDyanmicUrl,
    accept,
    mediaMessages,
    defaultMediaType = "image/png",
    variableMedia = false,
    setError
}) {
    // State management
    const [currentTab, setCurrentTab] = useState(0);
    const [mediaType, setMediaType] = useState(defaultMediaType);
    const [startcUrl, setStartcUrl] = useState("");
    const [dynamicUrl, setDynamicUrl] = useState("");
    const [dynamicUrlError, setDynamicUrlError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const clearError = () => {

        setError("");

    }
    const filePicekerRef = useRef(null);

    // Memoized file type mappings for better performance
    const fileTypeMapping = useMemo(() => ({
        pdf: "application/pdf",
        png: "image/png",
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        gif: "image/gif",
        mp4: "video/mp4"
    }), []);

    const fileExtensionMapping = useMemo(() => ({
        pdf: "file.pdf",
        png: "image.png",
        jpg: "image.jpeg",
        jpeg: "image.jpeg",
        gif: "image.gif",
        mp4: "video.mp4"
    }), []);

    // Enhanced utility functions with better error handling
    function getFileTypeFromUrl(url) {
        if (!url || typeof url !== 'string') {
            return "application/octet-stream";
        }

        const extension = url.split('.').pop()?.toLowerCase();
        return fileTypeMapping[extension] || "application/octet-stream";
    }

    function getFileExtensionFromUrl(url) {
        if (!url || typeof url !== 'string') {
            return "unknown";
        }

        const extension = url.split('.').pop()?.toLowerCase();
        return fileExtensionMapping[extension] || "unknown";
    }

    // Improved URL validation
    const isValidUrl = useCallback((string) => {
        try {
            new URL(string);
            return true;
        } catch (_) {
            return false;
        }
    }, []);

    // Enhanced URL selection with loading state and better error handling
    const onClickSelectUrl = useCallback(async () => {
        if (!startcUrl.trim()) {
            setError("Please enter a URL");
            return;
        }

        if (!isValidUrl(startcUrl)) {
            setError("Please enter a valid URL");
            return;
        }

        setIsLoading(true);
        clearError();


        try {
            const response = await apiGatewayAxiosInstance.get("/bot_management/campaign/read-url", {
                params: { url: startcUrl },
                timeout: 10000 // 10 second timeout
            });

            const fileType = getFileTypeFromUrl(startcUrl);
            const extension = getFileExtensionFromUrl(startcUrl);

            if (!response.data?.data) {
                throw new Error("Invalid response from server");
            }

            const byteArray = new Uint8Array(Object.values(response.data.data));
            const blob = new Blob([byteArray], { type: fileType });
            const file = new File([blob], extension, { type: fileType });

            inputOnChange(file);
        } catch (error) {
            console.error("Error fetching media from URL:", error);

            if (error.code === 'ECONNABORTED') {
                setError("Request timeout. Please try again with a smaller file or better connection.");
            } else if (error.response?.status === 404) {
                setError("File not found. Please check the URL and try again.");
            } else if (error.response?.status >= 400 && error.response?.status < 500) {
                setError("Access denied. Please check if the URL is publicly accessible.");
            } else {
                setError("Failed to fetch media from the provided URL. Please check the URL and try again.");
            }
        } finally {
            setIsLoading(false);
        }
    }, [startcUrl, inputOnChange, isValidUrl]);

    // Enhanced file change handler
    const handleFileChange = useCallback((value) => {
        onSelectImage(value);
        setOpenMediaDialog(false);
    }, [onSelectImage, setOpenMediaDialog]);

    // Improved dynamic URL validation and handling
    const onClickDynamicUrl = useCallback(() => {
        deleteFile();

        if (!dynamicUrl.trim()) {
            setDynamicUrlError("Please enter a dynamic URL");
            return;
        }

        if (!dynamicUrl.match(DYNAMIC_URL_REGEX)) {
            setDynamicUrlError("Please enter a valid dynamic URL in the format: https://example.com/[variable]");
            return;
        }
        handleDyanmicUrl({ url: dynamicUrl, type: mediaType });

        setOpenMediaDialog(false);
    }, [dynamicUrl, mediaType, handleDyanmicUrl, setOpenMediaDialog]);

    // Enhanced tab change handler
    const handleTabChange = useCallback((_, newValue) => {
        clearError();
        setCurrentTab(newValue);
        setDynamicUrlError("");
    }, []);

    // Dialog close handler
    const handleCloseDialog = useCallback(() => {
        setOpenMediaDialog(false);
        // Reset states when closing
        clearError();

        setDynamicUrlError("");
        setStartcUrl("");
        setDynamicUrl("");
        setIsLoading(false);
    }, [setOpenMediaDialog]);

    // File input click handler
    const handleFileInputClick = useCallback(() => {
        if (!isDisabled) {
            filePicekerRef.current?.click();
        }
    }, [isDisabled]);

    // Enhanced file input change handler
    const handleFileInputChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (file) {
            inputOnChange(file);
        }
        // Reset input value to allow selecting the same file again
        e.target.value = "";
    }, [inputOnChange]);

    // Determine available media types based on accept prop
    const availableMediaTypes = useMemo(() => {
        const types = [];
        if (accept.includes("image")) types.push({ value: "image/png", label: "Image" });
        if (accept.includes("video")) types.push({ value: "video/mp4", label: "Video" });
        if (accept.includes("application/pdf")) types.push({ value: "application/pdf", label: "PDF" });
        return types;
    }, [accept]);

    // Check if media is already selected
    const hasSelectedMedia = Boolean(previewImage?.url || formikImage?.url);



    const handleDrop = (event) => {
        event.preventDefault();
        const file = event.dataTransfer.files[0];
        inputOnChange(file);
    };



    return (
        <Dialog open={openMediaDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth aria-labelledby="select-media-dialog-title">
            <DialogTitle
                id="select-media-dialog-title"
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Typography variant="h5">Select Media</Typography>
                <IconButton onClick={handleCloseDialog} aria-label="close dialog" disabled={isLoading}>
                    <CloseOutlined />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Tabs value={currentTab} onChange={handleTabChange} aria-label="media selection tabs" sx={{ mb: 5 }}>
                    <Tab label="Upload Media" icon={<CloudUploadIcon />} iconPosition="start" disabled={isLoading} />
                    <Tab label="Import from URL" icon={<LinkIcon />} iconPosition="start" disabled={isLoading} />
                    {variableMedia && <Tab label="Variable Media URL" icon={<VariableIcon />} iconPosition="start" disabled={isLoading} />}
                </Tabs>

                {/* Upload Media Tab */}
                {currentTab === 0 && (
                    <Box>
                        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                            {mediaMessages?.Upload?.getDisplayText() || "Upload media files directly from your device"}
                        </Typography>

                        {hasSelectedMedia ? (
                            <Box sx={{ mb: 2 }}>

                                {
                                    (type == "application/pdf" || formikImage?.type === "application/pdf") ? (
                                        <MediaDisplay
                                            mediaUrl={formikImage?.url}
                                            fileName={formikImage?.fileName}
                                            handleRemoveMedia={deleteFile}
                                            disabled={Boolean(isDisabled)}
                                        />
                                    ) :
                                        <FilePreviewComponent
                                            fieldName={fieldName}
                                            state={state}
                                            url={url}
                                            type={type}
                                            previewImage={previewImage}
                                            deleteFile={deleteFile}
                                            formikImage={formikImage}
                                            isDisabled={isDisabled}
                                            aspect={aspect}
                                            onSelectImage={handleFileChange}
                                            width={width}
                                            height={height}
                                            imageHeightRatio={imageHeightRatio}
                                            imageWidthRatio={imageWidthRatio}
                                        />
                                }
                            </Box>
                        ) : (
                            <div onDrop={handleDrop} onDragOver={event => event.preventDefault()}>
                                <Box
                                    sx={{
                                        border: "2px dashed",
                                        borderColor: isDisabled ? "#e0e0e0" : "#ccc",
                                        borderRadius: "8px",
                                        padding: "40px 20px",
                                        textAlign: "center",
                                        cursor: isDisabled ? "not-allowed" : "pointer",
                                        color: isDisabled ? "#bdbdbd" : "#666",
                                        backgroundColor: isDisabled ? "#fafafa" : "transparent",
                                        transition: "all 0.2s ease-in-out",
                                        "&:hover": !isDisabled && {
                                            borderColor: "#999",
                                            backgroundColor: "#fafafa",
                                        },
                                    }}
                                    onClick={handleFileInputClick}
                                >
                                    <CloudUploadIcon sx={{ fontSize: 48, mb: 1, color: "inherit" }} />
                                    <Typography variant="body2">{isDisabled ? "Upload disabled" : "Click to upload or drag and drop"}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Supported formats: {accept}
                                    </Typography>
                                </Box>
                            </div>
                        )}
                        <input
                            disabled={isDisabled}
                            name={fieldName}
                            ref={filePicekerRef}
                            accept={accept}
                            onChange={handleFileInputChange}
                            type="file"
                            hidden
                            aria-label="File upload input"
                        />
                    </Box>
                )}

                {/* Import from URL Tab */}
                {currentTab === 1 && (
                    <Box>
                        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                            {mediaMessages?.UploadFromUrl?.getDisplayText() || "Import media from a public URL"}
                        </Typography>

                        {!hasSelectedMedia ? (
                            <Grid container spacing={2} sx={{ alignItems: "flex-start", mb: 2 }}>
                                <Grid item xs={12} sm={8}>
                                    <TextField
                                        fullWidth
                                        label="Public URL"
                                        placeholder="https://example.com/image.jpg"
                                        variant="outlined"
                                        value={startcUrl}
                                        onChange={e => {
                                            setStartcUrl(e.target.value);
                                            clearError();
                                        }}
                                        disabled={isLoading}
                                        error={Boolean(error)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={onClickSelectUrl}
                                        disabled={isLoading || !startcUrl.trim()}
                                        startIcon={isLoading ? <CircularProgress size={20} /> : <LinkIcon />}
                                        fullWidth
                                        sx={{ height: "56px" }}
                                    >
                                        {isLoading ? "Loading..." : "Import"}
                                    </Button>
                                </Grid>
                            </Grid>
                        ) : (

                            (type == "application/pdf" || formikImage?.type === "application/pdf") ? (
                                <MediaDisplay
                                    mediaUrl={formikImage?.url}
                                    fileName={formikImage?.fileName}
                                    handleRemoveMedia={deleteFile}
                                    disabled={Boolean(isDisabled)}
                                />
                            ) :
                                <FilePreviewComponent
                                    fieldName={fieldName}
                                    state={state}
                                    url={url}
                                    type={type}
                                    previewImage={previewImage}
                                    deleteFile={deleteFile}
                                    formikImage={formikImage}
                                    isDisabled={isDisabled}
                                    aspect={aspect}
                                    onSelectImage={handleFileChange}
                                    width={width}
                                    height={height}
                                    imageHeightRatio={imageHeightRatio}
                                    imageWidthRatio={imageWidthRatio}
                                />
                        )}
                    </Box>
                )}

                {/* Variable Media URL Tab */}
                {currentTab === 2 && (
                    <Box>
                        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                            {mediaMessages?.variableImageOrVideo?.getDisplayText() || "Use dynamic URLs with variables"}
                        </Typography>

                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Media Type
                            </Typography>
                            <RadioGroup row value={mediaType} onChange={e => setMediaType(e.target.value)} disabled={isLoading}>
                                {availableMediaTypes.map(({ value, label }) => (
                                    <FormControlLabel key={value} value={value} control={<Radio />} label={label} />
                                ))}
                            </RadioGroup>
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Variable URL"
                            placeholder="https://example.com/[variable]"
                            variant="outlined"
                            value={dynamicUrl}
                            onChange={e => {
                                setDynamicUrl(e.target.value);
                                setDynamicUrlError("");

                            }}
                            helperText={dynamicUrlError || "Use [variable] placeholder in your URL"}
                            error={Boolean(dynamicUrlError)}
                            disabled={isLoading}
                            sx={{ mb: 2 }}
                        />

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onClickDynamicUrl}
                            disabled={isLoading || !dynamicUrl.trim()}
                            startIcon={<CheckIcon />}
                        >
                            Select Variable URL
                        </Button>
                    </Box>
                )}

                {/* Error Messages */}
                {(error) && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    padding: "16px 24px",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "8px",
                }}
            >
                {/* <Button variant="outlined" onClick={handleCloseDialog} disabled={isLoading} color="error">
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleCloseDialog} disabled={isLoading} startIcon={<CheckIcon />}>
                    Done
                </Button> */}
            </DialogActions>
        </Dialog>
    );
}